import { ILoginProfile, ILoginRequestData, ILoginResponseData, ILoginResponseResolveData } from './models';

export interface IAuthenticatorService {
  login(
    email: string,
    password: string,
    remember?: boolean,
    profile?: ILoginProfile,
  ): Promise<ILoginResponseResolveData>;
  isAuthenticated(): Promise<boolean>;
  logOff(): Promise<void>;
  resetPassword(email: string): Promise<boolean>;
}

export class AuthenticatorService implements IAuthenticatorService {
  private readonly serviceUrl: string;

  constructor() {
    this.serviceUrl = `${this.getDomain()}/services/AuthenticationService/AuthenticationService.svc`;
  }

  public login(
    email: string,
    password: string,
    remember = false,
    profile?: ILoginProfile,
  ): Promise<ILoginResponseResolveData> {
    const data = {
      Login: email,
      Password: password,
      RememberMe: String(Number(remember || false)),
    } as ILoginRequestData;

    if (typeof profile === 'object' && profile !== null) {
      data.DuplicateProfileId = profile.CrmContactId || '';
    }

    return new Promise<ILoginResponseResolveData>(
      (resolve, reject): void => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${this.serviceUrl}/Authenticate`, true);
        xhr.setRequestHeader('content-type', 'application/json;charset=UTF-8');
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.onreadystatechange = (): void => {
          if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
            console.log('Login response successfully finished.');
          }
        };
        xhr.onload = (): void => {
          if (xhr.status === 200) {
            const response: ILoginResponseData = JSON.parse(xhr.response) as ILoginResponseData;
            const resolveData = {
              authenticated: (response.IsAuthenticated + '').toLowerCase() === 'true',
              customerName: response.CustomerName,
              requiresSelection: false,
            } as ILoginResponseResolveData;

            if (resolveData.authenticated) {
              if (response.DuplicateProfiles && response.DuplicateProfiles.length > 1) {
                resolveData.profiles = response.DuplicateProfiles;
                resolveData.requiresSelection = (data.DuplicateProfileId || '').length === 0;
              }
            }

            resolve(resolveData);
          } else {
            reject(xhr.status);
          }
        };
        xhr.onerror = (): void => {
          reject(Error('Network error'));
        };

        xhr.send(JSON.stringify(data));
      },
    );
  }

  public isAuthenticated(): Promise<boolean> {
    return new Promise(
      (resolve): void => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${this.serviceUrl}/IsAuthenticated?cache=${new Date().getTime()}`, true);
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.onload = (): void => {
          if (xhr.status === 200) {
            resolve(xhr.response === 'true');
          } else {
            resolve(false);
          }
        };
        xhr.onerror = (): void => {
          resolve(false);
        };

        xhr.send();
      },
    );
  }

  public logOff(): Promise<void> {
    return new Promise(
      (resolve, reject): void => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${this.serviceUrl}/Logoff?cache=${new Date().getTime()}`, true);
        xhr.onload = (): void => {
          resolve();
        };
        xhr.onerror = (): void => {
          reject();
        };

        xhr.send();
      },
    );
  }

  public resetPassword(email: string): Promise<boolean> {
    return new Promise(
      (resolve, reject): void => {
        if (typeof email !== 'string' || email === '') {
          reject(false);
        }

        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${this.serviceUrl}/ResetPassword/${email}`, true);
        xhr.onload = (): void => {
          if (xhr.status === 200) {
            resolve(Boolean(xhr.response));
          } else {
            resolve(false);
          }
        };
        xhr.onerror = (): void => {
          reject(false);
        };

        xhr.send();
      },
    );
  }

  private getDomain(): string {
    const location: Location = window.location;
    const host: string = location.host;

    if (host && host.indexOf('.jabra.') > 0) {
      return 'https://' + host;
    }

    if (host === 'jabra-public-shell-en' || host === 'jabra-public-phobos-shell-en.jabra.lcl') {
      return 'http://' + host;
    }

    return 'https://www.jabra.com';
  }
}
